import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type {
  PerformanceCategory,
  TakataDealersKpis,
  ThresholdInfo,
} from './useTakataDealers';

/**
 * Data access for the main Stellantis dealer dashboard.
 *
 * The Takata twin is `useTakataDealers`. The two campaigns read different tables
 * and show different columns, so the row types differ, but the surrounding
 * vocabulary — categories, thresholds, KPIs — is shared rather than redeclared.
 */

const BASE = '/api/stla/dealers';

/** One dealer in one brand. Physical dealers repeat per brand they carry. */
export interface Dealer {
  name: string;
  country: string;
  city: string;
  brand: string;
  address: string;
  phone: string;
  assignedBrand: string;
  carType: string;
  latitude: number;
  longitude: number;
  totalCalls: number;
  answeredCalls: number;
  averageWaitDays: number | null;
  categoryIndex: number | null;
  performanceColor: string;
  answerRate: number;
  oic: string;
  physOut: string;
  zipCode: string;
  nuts3Code: string;
  hasAppointment: boolean;
  dealerService: string;
}

/**
 * A garage that was called in the wave but never booked an appointment.
 *
 * Not a dealer and not plotted — it has no coordinates and no performance
 * category — but its calls count towards the Total Calls tile, which is why the
 * client needs the rows rather than just a number: the tile is recomputed under
 * every filter.
 */
export interface NoAppointmentCalls {
  name: string;
  country: string;
  city: string;
  brand: string;
  carType: string;
  dealerService: string;
  totalCalls: number;
  answeredCalls: number;
}

export interface DealersDashboard {
  availableWaves: string[];
  selectedWave: string | null;
  kpis: TakataDealersKpis;
  dealers: Dealer[];
  noAppointmentCalls: NoAppointmentCalls[];
  countries: string[];
  cities: string[];
  brands: string[];
  performanceCategories: Record<string, PerformanceCategory>;
  thresholdsByCountry: Record<string, ThresholdInfo[]>;
  countryCodes: Record<string, string>;
  /** City to country, for narrowing the city suggestions to a selected market. */
  cityToCountry: Record<string, string>;
}

export const dealerKeys = {
  all: ['stla', 'dealers'] as const,
  dashboard: (wave: string | null) => ['stla', 'dealers', 'dashboard', wave] as const,
};

/** The whole dashboard for one wave; null wave means the most recent one. */
export function useDealersDashboard(wave: string | null) {
  return useQuery<DealersDashboard>({
    queryKey: dealerKeys.dashboard(wave),
    queryFn: () => api.get<DealersDashboard>(BASE, { query: { wave } }),
    // A wave is closed data between imports, and this is a large payload.
    staleTime: 5 * 60 * 1000,
  });
}
